import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router";
import PrimeDialog from "../../../components/dialog/PrimeDialog";
import { FormInput } from "../../../components/FormInput";
import FormNumberInput from "../../../components/FormNumberInput";
import NoInfo from "../../../components/NoInfo";
import PrimePanel from "../../../components/PrimePanel";
import { useEditableFlag } from "../../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../../store/messageStore";
import {
  F106031101Before,
  queryCbamProductSummary,
  updateCbamProductSummary,
} from "../../../utility/service/cbam-service";
import { getCBAMSubItemsByItemNo } from "../../../utility/service/companyOffering-service";
import { Base64Encoder, BcryptEncoder, checkDpSuccess } from "../../../utility/service/tool-service";
import { addFormValidator } from "../../../utility/validate/formValidator";
import { BigNumber } from "bignumber.js";
import NumberUtil from "../../../utility/NumberUtil";
import { Tooltip } from "primereact/tooltip";

const ProductOverview = () => {
  const { t } = useTranslation();
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const { currentCompany, currentCbamMasterSeq } = useOutletContext();
  const [formRule, setFormRule] = useState([]); //表單驗證 設備
  const isEditable = useEditableFlag("F10603");
  const [rowData, setRowData] = useState([]);
  const [tabAction, setTabAction] = useState("detail");
  const [editDialog, setEditDialog] = useState(false);
  const [reducingAgentList, setReducingAgentList] = useState([]);
  const [priceToolList, setPriceToolList] = useState([]);
  const [currencyList, setCurrencyList] = useState([]);
  const [offestTypeList, setOffestTypeList] = useState([]);
  const [cnCodeList, setCnCodeList] = useState([]);

  const {
    getValues,
    setValue,
    watch,
    control,
    register,
    handleSubmit,
    clearErrors,
    trigger,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  useEffect(() => {
    axios_F106031101Before();
    axios_queryCbamProductSummary();
    axios_getCBAMSubItemsByItemNo("REDUCING_AGENT", 19).then(setReducingAgentList);
    axios_getCBAMSubItemsByItemNo("PRICE_TOOL", 18).then(setPriceToolList);
    axios_getCBAMSubItemsByItemNo("CBAM_CURRENCIES", 1).then(setCurrencyList);
    axios_getCBAMSubItemsByItemNo("OFFEST_TYPE", 20).then(setOffestTypeList);
    axios_getCBAMSubItemsByItemNo("CBAM_CNC", 3).then(setCnCodeList);
  }, []);

  const axios_getCBAMSubItemsByItemNo = (itemNo, orderNo) => {
    return getCBAMSubItemsByItemNo(itemNo, orderNo).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        return data.map((item, index) => ({
          ...item,
          index,
        }));
      }
      return [];
    });
  };

  const axios_F106031101Before = () => {
    checkLoading(loading);
    F106031101Before()
      .then((res) => {
        if (checkDpSuccess(res)) {
          setFormRule(processConstraints(res?.data?.RespBody?.constraints));
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  const setValidForm = useCallback(
    (formId) => {
      if (formRule?.length > 0) {
        const tmp = formRule?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [formRule],
  );

  const processConstraints = (constraints) => {
    return constraints.map((item) => {
      if (item?.subObject?.value) {
        return { [item?.field]: processConstraints(item?.subObject.value) };
      } else {
        return addFormValidator(item?.field, constraints);
      }
    });
  };

  // 產品匯總查詢
  const axios_queryCbamProductSummary = () => {
    checkLoading(loading);
    queryCbamProductSummary({
      companySeq: currentCompany?.companySeq,
      cbamMasterSeq: currentCbamMasterSeq,
    })
      .then((res) => {
        if (checkDpSuccess(res)) {
          const data = res?.data.RespBody?.cbamProductSummaryList ?? [];
          data.forEach((item) => {
            const seeTotal = new BigNumber(item.SEETotal || 0);
            const newVal = new BigNumber(item.offsetRatio || 0).div(100);
            item.offsetEmissions =  NumberUtil.formatWithCommas(seeTotal.times(newVal), 0, 3);
          });
          setRowData(res?.data.RespBody?.cbamProductSummaryList ?? []);
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  const HeaderTemplate = ({ options, field, index }) => {
    return (
      <div className={`${options.className}`} key={field.cbamDetailSeq}>
        <Badge value={index + 1} style={{ marginRight: "0.5rem" }} />
        <div className="grid w-full font-bold">
          <div className="col-12 lg:col-4">
            <label className="label-has-icon">{t("declaredProduct")}:</label>
            {field?.displayOfferingsName ?? ""}
          </div>
          <div className="col-12 lg:col-4">
            <label className="label-has-icon">{t("productionProcess")}:</label>
            {field?.displayEquipmentName ?? ""}
          </div>
          <div className="col-12 lg:col-4">
            <label className="label-has-icon">{t("relProdCat")}:</label>
            {field?.displayGoodCategoryName ?? ""}
          </div>
        </div>

        <Button
          tooltip={t("button.edit")}
          disabled={!isEditable || tabAction === "edit"}
          tooltipOptions={{ position: "top" }}
          className="p-button-icon-only"
          rounded
          text
          severity="secondary"
          style={{ margin: "0px", padding: "0px", height: "32px", width: "30px" }}
          onClick={() => {
            editItem(field);
          }}
        >
          <FontAwesomeIcon icon="fa-solid fa-edit" />
        </Button>
        {options.togglerElement}
      </div>
    );
  };

  const summaryFields = [
    { value: "cnCode", title: `CN Code`, type: "dropdown", options: cnCodeList, displayCode: true },
    {
      value: "precursorReducingAgent",
      title: t("precursorReducingAgent"),
      type: "dropdown",
      options: reducingAgentList,
    },
    { value: "steelPlantIdentification", title: t("steelPlantId"), type: "text" },
    {
      value: "priceTool",
      title: t("priceTool"),
      type: "dropdown",
      options: priceToolList,
    },
    {
      value: "carbonPriceCoverageRatio",
      title: t("carbonPriceCoverageRatio"),
      type: "number",
    },
    { value: "payCarbonPrice", title: t("payCarbonPrice"), type: "number" },
    {
      value: "payCarbonPriceUnit",
      title: t("payCarbonPriceUnit"),
      type: "dropdown",
      options: currencyList,
    },
    {
      value: "offsetType",
      title: t("offsetType"),
      type: "dropdown",
      options: offestTypeList,
    },
    {
      value: "offsetRatio",
      title: t("offsetRatio"),
      type: "number",
      onChange: (newValue) => {
        const seeTotal = new BigNumber(getValues("SEETotal") || 0);
        const newVal = new BigNumber(newValue || 0).div(100);

        const result = NumberUtil.formatWithCommas(seeTotal.times(newVal), 0, 3);
        setValue("offsetEmissions", result);
      },
    },
    {
      value: "offsetEmissions",
      tip: true,
      title: `${t("offsetEmissions")}(tCO2e/t)`,
    },
    {
      value: "offsetAmount",
      title: `${t("offsetAmount")}(${t("payCarbonPriceUnit")}/t)`,
      type: "number",
    },
    { value: "mnPercent", title: `${t("mnPercent")}%`, type: "number" },
    { value: "crPercent", title: `${t("crPercent")}%`, type: "number" },
    { value: "niPercent", title: `${t("niPercent")}%`, type: "number" },
    { value: "otherMetalPercent", title: `${t("otherMetalPercent")}%`, type: "number" },
    { value: "carbonPercent", title: `${t("carbonPercent")}%`, type: "number" },
    {
      value: "otherMaterialPercent",
      title: `${t("otherMaterialPercent")}%`,
      type: "number",
    },
    {
      value: "preConsumerWastePercent",
      title: `${t("preConsumerWastePercent")}%`,
      type: "number",
    },
    { value: "noAlPercent", title: `${t("noAlPercent")}%`, type: "number" },
    { value: "nitricAcidPercent", title: `${t("nitricAcidPercent")}%`, type: "number" },
    { value: "ureaPercent", title: `${t("ureaPercent")}%`, type: "number" },
    { value: "nitrogenPercent", title: `${t("nitrogenPercent")}%`, type: "number" },
    { value: "clinkerIngredients", title: t("clinkerFactor"), type: "number" },
    { value: "concentration", title: t("concentration"), type: "number" },
    {
      value: "alContainWastePercent",
      title: `${t("alContainWastePerTon")}(t)`,
      type: "number",
    },
    {
      value: "recyclingScrapSteel",
      title: `${t("recyclingScrapSteel")}(t)`,
      type: "number",
    },
    { value: "ammoniumNPercent", title: `${t("ammoniumNPercent")}%`, type: "number" },
    { value: "nitrateNPercent", title: `${t("nitrateNPercent")}%`, type: "number" },
    { value: "ureaNPercent", title: `${t("ureaNPercent")}%`, type: "number" },
    { value: "otherNPercent", title: `${t("otherNPercent")}%`, type: "number" },
    { value: "isCalcination", title: t("isCalcination"), type: "checkbox" },
  ];

  const editItem = (data) => {
    setEditDialog(true);
    setTabAction("edit");
    Object.entries(data).forEach(([key, value]) => {
      setValue(key, value, { shouldValidate: true });
    });
  };

  const celarEdit = () => {
    setTabAction("detail");
    setEditDialog(false);
    clearErrors();
    reset();
  };

  const renderer = (fieldData) => {
    switch (fieldData.type) {
      case "text":
        return (
          <FormInput
            register={register}
            errors={errors}
            id={fieldData.value}
            name={fieldData.value}
            wrapperClass="w-full"
            rules={setValidForm(fieldData.value)}
            value={watch(fieldData.value) || ""}
          />
        );
      case "checkbox":
        return (
          <Controller
            name={`${fieldData.value}`}
            control={control}
            render={({ field }) => (
              <Checkbox
                checked={watch(fieldData.value) === "Y"}
                onChange={(e) => field.onChange(e.checked ? "Y" : "N")}
              />
            )}
          />
        );
      case "number":
        return (
          <FormNumberInput
            register={register}
            errors={errors}
            id={fieldData.value}
            name={fieldData.value}
            rules={setValidForm(fieldData.value)}
            wrapperClass="w-full"
            value={watch(fieldData.value)}
            onChange={(newValue) => {
              setValue(fieldData.value, newValue);
              fieldData?.onChange?.(newValue);
            }}
          />
        );
      case "dropdown":
        return (
          <Controller
            name={fieldData.value}
            control={control}
            rules={setValidForm(fieldData.value)}
            render={({ field }) => (
              <Dropdown
                tooltip={fieldData.options?.find((opt) => opt.subitemNo === watch(fieldData.value))?.subitemName || ""}
                tooltipOptions={{ position: "top" }}
                disabled={!isEditable}
                filter
                showClear
                optionLabel="subitemName"
                optionValue="subitemNo"
                value={watch(fieldData.value)}
                virtualScrollerOptions={{ itemSize: 40 }}
                defaultValue={""}
                placeholder={t("plz_chs")}
                name={fieldData.value}
                control={control}
                options={fieldData.options}
                className={`w-full ` + classNames({ "p-invalid": field.error })}
                onChange={(event) => {
                  field.onChange(event.value);
                }}
              />
            )}
          />
        );
      default:
        return <span className={`w-full text-right`}>{watch(fieldData.value)}</span>;
    }
  };

  const encoder = (dataList, code) => {
    if (code === null || code === undefined || code === "") return "";
    if (dataList === null || dataList === undefined || dataList === "" || dataList.length <= 0) return "";
    return Base64Encoder(BcryptEncoder(code ?? "")) + "," + findItemIndex(dataList, code ?? "");
  };

  const findItemIndex = (array, code) => {
    let result = array?.filter((item) => item.subitemNo === code);
    return result.length > 0 ? result[0].index : false;
  };

  const onSubmit = () => {
    const reqBody = {
      cbamProductSummarySeq: getValues("cbamProductSummarySeq"),
      cbamMasterSeq: getValues("cbamMasterSeq"),
      cbamDetailSeq: getValues("cbamDetailSeq"),
      cbamProcessDataSeq: getValues("cbamProcessDataSeq"),
      cbamRouteDataSeq: getValues("cbamRouteDataSeq"),
      cnCode: encoder(cnCodeList, getValues("cnCode")),
      precursorReducingAgent: encoder(reducingAgentList, getValues("precursorReducingAgent")),
      priceTool: encoder(priceToolList, getValues("priceTool")),
      carbonPriceCoverageRatio: getValues("carbonPriceCoverageRatio"),
      payCarbonPrice: getValues("payCarbonPrice"),
      payCarbonPriceUnit: encoder(currencyList, getValues("payCarbonPriceUnit")),
      offsetType: encoder(offestTypeList, getValues("offsetType")),
      offsetRatio: getValues("offsetRatio"),
      offsetAmount: getValues("offsetAmount"),
      mnPercent: getValues("mnPercent"),
      crPercent: getValues("crPercent"),
      niPercent: getValues("niPercent"),
      otherMetalPercent: getValues("otherMetalPercent"),
      carbonPercent: getValues("carbonPercent"),
      recyclingScrapSteel: getValues("recyclingScrapSteel"),
      otherMaterialPercent: getValues("otherMaterialPercent"),
      preConsumerWastePercent: getValues("preConsumerWastePercent"),
      alContainWastePercent: getValues("alContainWastePercent"),
      noAlPercent: getValues("noAlPercent"),
      clinkerIngredients: getValues("clinkerIngredients"),
      concentration: getValues("concentration"),
      nitricAcidPercent: getValues("nitricAcidPercent"),
      ureaPercent: getValues("ureaPercent"),
      nitrogenPercent: getValues("nitrogenPercent"),
      ammoniumNPercent: getValues("ammoniumNPercent"),
      nitrateNPercent: getValues("nitrateNPercent"),
      ureaNPercent: getValues("ureaNPercent"),
      otherNPercent: getValues("otherNPercent"),
      steelPlantIdentification: getValues("steelPlantIdentification"),
      isCalcination: getValues("isCalcination"),
    };
    checkLoading(loading);
    updateCbamProductSummary(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
          celarEdit();
          axios_queryCbamProductSummary();
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  return (
    <>
      {rowData.length === 0 && <NoInfo />}

      <div className="grid">
        {rowData.map((fieldData, index) => {
          const chunkFields = (fields, size) => {
            return fields.reduce((acc, _, i) => (i % size === 0 ? [...acc, fields.slice(i, i + size)] : acc), []);
          };
          const rows = chunkFields(summaryFields, 3);

          return (
            <div className="col-12" key={index}>
              <PrimePanel
                toggleable
                className="dataTable-panel evaluation-temp-setting-panel"
                headerTemplate={(options) => <HeaderTemplate options={options} field={fieldData} index={index} />}
              >
                <div
                  className="white-data-table hover-highlight no-shadow p-datatable p-datatable-striped"
                  style={{ overflowX: "auto" }}
                >
                  <table className="p-datatable-table">
                    <tbody className="p-datatable-tbody">
                      {rows.map((rowFields, rowIndex) => {
                        const isLastRow = rowIndex === rows.length - 1;

                        return (
                          <tr key={`row-${rowIndex}`} className={`${rowIndex % 2 === 0 ? "" : "p-row-odd"}`}>
                            {Array.from({ length: 3 }).map((_, colIndex) => {
                              const field = rowFields[colIndex];

                              if (!field) {
                                return (
                                  <React.Fragment key={`empty-${colIndex}`}>
                                    <td style={{ borderBottom: isLastRow ? "none" : "1px solid var(--gray-200)" }} />
                                    <td style={{ borderBottom: isLastRow ? "none" : "1px solid var(--gray-200)" }} />
                                  </React.Fragment>
                                );
                              }

                              // 處理數值顯示邏輯
                              const displayValue =
                                field.type === "dropdown"
                                  ? `${field.options?.find((opt) => opt.subitemNo === fieldData[field.value])
                                      ?.subitemName ?? ""}${
                                      field.displayCode && fieldData[field.value] ? ` (${fieldData[field.value]})` : ""
                                    }`
                                  : fieldData[field.value];

                              const isRightAlign = field.type === "number" || field.value === "offsetEmissions";

                              return (
                                <React.Fragment key={field.value || colIndex}>
                                  <td
                                    style={{
                                      padding: "0.5rem",
                                      width: "150px",
                                      minWidth: "150px",
                                      fontWeight: "bold",
                                      borderBottom: isLastRow ? "none" : "1px solid var(--gray-200)",
                                    }}
                                  >
                                    {field.title}
                                    {(field.tip ?? false) && (
                                      <span
                                        key="icon"
                                        className="form-exclamation"
                                        data-pr-tooltip={`( ${t("seeDirect")}${
                                          fieldData.SEEDirect ? `(${fieldData.SEEDirect})` : ""
                                        } + ${t("seeInDirect")}${
                                          fieldData.SEEInDirect ? `(${fieldData.SEEInDirect})` : ""
                                        } ) x ${t("offsetRatio")} = ${t("offsetEmissions")}`}
                                        style={{ "--bg-color": "var(--primary-color)" }}
                                      ></span>
                                    )}
                                  </td>
                                  <td
                                    className={isRightAlign ? "text-right" : ""}
                                    style={{
                                      padding: "0.5rem",
                                      width: "200px",
                                      minWidth: "200px",
                                      borderBottom: isLastRow ? "none" : "1px solid var(--gray-200)",
                                    }}
                                  >
                                    {displayValue}
                                  </td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Tooltip target=".form-exclamation" />
              </PrimePanel>
            </div>
          );
        })}
      </div>

      {/* 編輯 */}
      <PrimeDialog
        visible={editDialog}
        title={t("button.edit")}
        size="lg"
        onClose={() => {
          celarEdit();
        }}
        onConfirm={onSubmit}
        disabledConfirm={!(isValid && isDirty)}
        bodyTemplate={
          <div style={{ minWidth: "850px" }}>
            {/* 上方基本資訊*/}
            <div className="pb-3">
              <div className="p-card bg-gray">
                <div className="p-card-body">
                  <div className="grid align-items-center">
                    <div className="col-4">
                      <label className="label-has-icon">{t("declaredProduct")}:</label>
                      <span>{getValues("displayOfferingsName")}</span>
                    </div>
                    <div className="col-4">
                      <label className="label-has-icon">{t("productionProcess")}:</label>
                      <span>{getValues("displayEquipmentName")}</span>
                    </div>
                    <div className="col-4">
                      <label className="label-has-icon">{t("relProdCat")}:</label>
                      <span>{getValues("displayGoodCategoryName")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Tooltip key="tooltip" target=".form-exclamation" position="top" />
            <div
              className="grid-table"
              style={{
                "--body-font-size": "0.875rem",
                border: "1px solid var(--gray-200)",
                gridTemplateColumns: `repeat(6, minmax(0, 1fr))`,
              }}
            >
              {summaryFields.map((field, index) => {
                const isLastRow = index >= summaryFields.length - (summaryFields.length % 6 || 6);
                return (
                  <React.Fragment key={`${field.value}`}>
                    <div
                      className="grid-table-header"
                      style={{
                        background: "white",
                        border: "none",
                        borderBottom: isLastRow ? "none" : "1px solid var(--gray-200)",
                      }}
                    >
                      <span className="w-full">
                        {field.title}
                        {(field.tip ?? false) && (
                          <span
                            key="icon"
                            className="form-exclamation"
                            data-pr-tooltip={`( ${t("seeDirect")}${
                              getValues("SEEDirect") ? `(${getValues("SEEDirect")})` : ""
                            } + ${t("seeInDirect")}${
                              getValues("SEEInDirect") ? `(${getValues("SEEInDirect")})` : ""
                            } ) x ${t("offsetRatio")} = ${t("offsetEmissions")}}`}
                            style={{ "--bg-color": "var(--primary-color)" }}
                          ></span>
                        )}
                      </span>
                    </div>
                    <div
                      className="grid-table-col"
                      style={{
                        border: "none",
                        borderBottom: isLastRow ? "none" : "1px solid var(--gray-200)",
                      }}
                    >
                      {renderer(field)}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        }
      ></PrimeDialog>
    </>
  );
};
export default ProductOverview;
